import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check, X, Hand, UserPlus } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useLiveRoomEvents } from "@/hooks/useLiveRoomWS";
import UserBadge from "@/components/UserBadge";

/**
 * LiveRoomCohostRequests — small drawer + trigger pill for the HOST
 * showing viewer-initiated "Request to co-host" submissions. Approving a
 * request auto-invites + accepts in one shot on the backend, so the
 * requester's token refresh promotes them to co-host on their next poll.
 *
 * Renders only for the host. Empty state is hidden entirely.
 *
 * Phase 2 — the 15-second poll is now a safety net. The primary trigger
 * is a WS `cohost:request` frame which lands the request in the drawer
 * within ~100ms of the raise-hand tap.
 */
export default function LiveRoomCohostRequests({ streamId, isHost, actorRole }) {
  // Pair 3: host + Admin accept/deny; Moderators may only deny (remove waiting guests).
  const actor = actorRole || (isHost ? "host" : "viewer");
  const canSee = actor === "host" || actor === "admin" || actor === "moderator";
  const canAccept = actor === "host" || actor === "admin";
  const [open, setOpen] = useState(false);
  const [requests, setRequests] = useState([]);
  const [boxes, setBoxes] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!canSee) return;
    try {
      const { data } = await api.get(`/live/${streamId}/cohost/requests`);
      setRequests(data?.requests || []);
      setBoxes(data ? { available: data.boxes_available, capacity: data.capacity } : null);
    } catch {
      /* silent — drawer just stays empty */
    }
  }, [canSee, streamId]);

  // Creator Control Center cell asks us to open via a window event.
  useEffect(() => {
    if (!canSee) return undefined;
    const onOpen = () => setOpen(true);
    window.addEventListener("pandax:open-cohost-requests", onOpen);
    return () => window.removeEventListener("pandax:open-cohost-requests", onOpen);
  }, [canSee]);
  useEffect(() => {
    if (!canSee) return undefined;
    window.dispatchEvent(new CustomEvent("pandax:cohost-requests-count", { detail: requests.length }));
  }, [canSee, requests.length]);

  useEffect(() => {
    if (!canSee) return;
    let cancelled = false;
    const tick = async () => { if (!cancelled) await load(); };
    tick();
    // Safety-net poll dropped to 30s — WS drives the fast path.
    const iv = setInterval(tick, 30_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [canSee, load]);

  // Phase 2 — WS fast path. Any cohost:request/accept/decline/invite
  // for this room reloads the drawer immediately.
  useLiveRoomEvents((msg) => {
      if (
        msg?.channel === "cohost:request" ||
        msg?.channel === "cohost:accept" ||
        msg?.channel === "cohost:decline" ||
        msg?.channel === "cohost:invite"
      ) {
        load();
      }
  }, { enabled: canSee });

  if (!canSee) return null;

  const respond = async (req, accept) => {
    if (busyId) return;
    setBusyId(req.id);
    try {
      await api.post(`/live/cohost/requests/${req.id}/respond`, { accept });
      if (accept) toast.success(`Brought ${req.requester?.display_name} up`);
      else toast("Request denied.");
      setRequests((prev) => prev.filter((r) => r.id !== req.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not respond.");
    } finally {
      setBusyId(null);
    }
  };

  if (requests.length === 0 && !open) return null; // no requests + not open

  return (
    <>
      {/* Trigger pill — lives just above the action rail, only shows if
          there's at least one pending request. Pulsing amber dot for glance-ability. */}
      {requests.length > 0 && !open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="cohost-requests-pill"
          className="absolute left-3 [[data-stage-side=left]_&]:left-[calc(66%+0.5rem)] z-30 flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full px-3 py-2 text-xs shadow-xl"
          style={{ bottom: "max(5rem, calc(env(safe-area-inset-bottom) + 4.5rem))" }}
        >
          <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
          <Hand size={12} />
          {requests.length} {actor === "moderator" ? "waiting guest" : "co-host request"}{requests.length === 1 ? "" : "s"}
        </button>
      )}

      {open && (
        <div
          data-testid="cohost-requests-drawer"
          className="absolute inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center"
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md bg-zinc-950 border border-white/10 rounded-t-2xl sm:rounded-2xl max-h-[70vh] overflow-hidden flex flex-col"
          >
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserPlus size={14} className="text-amber-400" />
                <div className="text-sm font-semibold text-white">
                  Co-host requests
                </div>
                {boxes && (
                  <span data-testid="cohost-requests-boxes" className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${boxes.available > 0 ? "border-emerald-500/40 text-emerald-300" : "border-rose-500/40 text-rose-300"}`}>
                    {boxes.available} of {boxes.capacity} box{boxes.capacity === 1 ? "" : "es"} free
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="cohost-requests-close"
                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white flex items-center justify-center"
                aria-label="Close"
              >
                <X size={14} />
              </button>
            </div>
            {requests.length === 0 ? (
              <div className="p-8 text-center text-sm text-zinc-400">
                No pending requests. Viewers who want to jump on stage will show up here.
              </div>
            ) : (
              <ul className="divide-y divide-white/5 overflow-y-auto">
                {requests.map((r) => (
                  <li
                    key={r.id}
                    className="flex items-start gap-3 px-4 py-3"
                    data-testid={`cohost-request-row-${r.requester?.id}`}
                  >
                    <UserBadge
                      userRef={r.requester?.id || r.requester?.handle}
                      className="flex items-start gap-3 min-w-0 flex-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded-md text-left"
                      aria-label={`View ${r.requester?.display_name}'s profile`}
                    >
                      <Avatar className="w-11 h-11 border border-white/10">
                        <AvatarImage src={r.requester?.avatar_url} />
                        <AvatarFallback className="bg-rose-600 text-white text-xs">
                          {r.requester?.display_name?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0" data-testid={`cohost-request-preview-${r.requester?.id}`}>
                        <div className="text-sm text-white truncate flex items-center gap-1.5">
                          {r.requester?.display_name || r.requester?.handle}
                          {r.requester?.is_creator && <span className="text-[8px] uppercase tracking-widest px-1 py-px border border-rose-500/40 text-rose-300">Creator</span>}
                          {r.requester?.mutual && <span className="text-[8px] uppercase tracking-widest px-1 py-px border border-emerald-500/40 text-emerald-300">Mutual</span>}
                        </div>
                        <div className="text-[11px] text-zinc-500 truncate">
                          {r.requester?.display_name} · {r.requester?.followers_count ?? 0} followers · {r.requester?.gifted_this_live ?? 0} coins gifted here
                          {r.requester?.follows_host && !r.requester?.mutual ? " · follows you" : ""}
                        </div>
                        {r.requester?.bio ? <div className="text-[11px] text-zinc-400 line-clamp-2 mt-0.5">{r.requester.bio}</div> : null}
                      </div>
                    </UserBadge>
                    <button
                      type="button"
                      onClick={() => respond(r, false)}
                      disabled={busyId === r.id}
                      data-testid={`cohost-request-deny-${r.requester?.id}`}
                      className="w-8 h-8 rounded-full bg-transparent border border-rose-500/40 hover:bg-rose-500/10 disabled:opacity-60 text-rose-300 flex items-center justify-center"
                      aria-label="Deny"
                    >
                      <X size={14} />
                    </button>
                    {canAccept && <button
                      type="button"
                      onClick={() => respond(r, true)}
                      disabled={busyId === r.id}
                      data-testid={`cohost-request-accept-${r.requester?.id}`}
                      className="w-8 h-8 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-black flex items-center justify-center"
                      aria-label="Accept"
                    >
                      {busyId === r.id ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : (
                        <Check size={13} />
                      )}
                    </button>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}
